import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectActor, selectCurrentUser, selectIsAuthenticated, selectAuthLoading } from '../../../store/auth/auth.selectors';
import { CurrencyService, Currency } from '../../../core/services/currency.service';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { ThemeSwitcherComponent } from '../../../shared/components/theme-switcher/theme-switcher.component';
import { ThemeService } from '../../../core/services/theme.service';
import { Subject, combineLatest, forkJoin, takeUntil } from 'rxjs';
import { map, take } from 'rxjs/operators';
import * as AuthActions from '../../../store/auth/auth.actions';
import { FilesService } from '../../../core/services/files.service';

@Component({
    selector: 'app-public-header',
    standalone: true,
    imports: [CommonModule, RouterLink, RouterLinkActive, LucideIconsModule, ThemeSwitcherComponent],
    templateUrl: './public-header.component.html',
    styleUrls: ['./public-header.component.scss'],
})
export class PublicHeaderComponent implements OnInit, AfterViewInit, OnDestroy {
    private store = inject(Store);
    public currencyService = inject(CurrencyService);
    private router = inject(Router);
    readonly themeService = inject(ThemeService);
    private filesService = inject(FilesService);
  private destroy$ = new Subject<void>();
  private elementRef = inject(ElementRef);
  private headerResizeObserver: ResizeObserver | null = null;

    readonly logoBlackSrc = 'https://assetsnibni.zohostratus.com/images/logos/nibni-logo-black.avif';
    readonly logoWhiteSrc = 'https://assetsnibni.zohostratus.com/images/logos/nibni-logo-white.avif';

    isAuthenticated$ = this.store.select(selectIsAuthenticated);
    loading$ = this.store.select(selectAuthLoading);
    user$ = this.store.select(selectCurrentUser);
    actorLabel$ = combineLatest([this.store.select(selectActor), this.store.select(selectCurrentUser)]).pipe(
        map(([actor, user]) => {
            if (!actor) return 'Account';
            if (actor.actor_type === 'investor') return 'Investor';
            const orgName = String(user?.company_name || user?.companyName || user?.org_name || user?.orgName || '').trim();
            return orgName || 'Organization';
        })
    );

    openSignup = false;
    openProfile = false;
    openCurrency = false;
    openMobile = false;
    showDisplayCurrencyPrompt = false;
    private lastAvatarUserId: string | null = null;
    private lastAvatarFileId: string | null = null;
    private readonly displayCurrencyPromptKey = 'nibni_display_currency_prompt_shown';

    navigation = [
        { name: "Properties", href: "/properties" },
        { name: "Projects", href: "/projects" },
        { name: "FAQ", href: "/faq" },
    ];

    currencies: Currency[] = ['USD', 'EUR', 'GBP', 'AED', 'SAR', 'EGP', 'QAR'] as Currency[];
    avatarLoadFailed = false;
    avatarUrl: string | null = null;

    get logoSrc(): string {
        const isDarkTheme = this.themeService.theme() !== 'white';
        return isDarkTheme ? this.logoWhiteSrc : this.logoBlackSrc;
    }

    toggleSignup() {
        this.openSignup = !this.openSignup;
        this.openProfile = false;
        this.openCurrency = false;
    }

    toggleProfile() {
        this.openProfile = !this.openProfile;
        this.openSignup = false;
        this.openCurrency = false;
    }

    toggleCurrency() {
        this.openCurrency = !this.openCurrency;
        this.openSignup = false;
        this.openProfile = false;
    }

    selectCurrency(currency: Currency) {
        const current = this.currencyService.currentCurrency();
        if (current === currency) {
            this.openCurrency = false;
            return;
        }
        this.currencyService.setCurrency(currency);
        this.openCurrency = false;
        this.maybeShowDisplayCurrencyPrompt();
    }

    private maybeShowDisplayCurrencyPrompt(): void {
        try {
            if (localStorage.getItem(this.displayCurrencyPromptKey)) return;
            localStorage.setItem(this.displayCurrencyPromptKey, '1');
        } catch {
            // ignore storage failures
        }
        this.showDisplayCurrencyPrompt = true;
    }

    closeDisplayCurrencyPrompt(): void {
        this.showDisplayCurrencyPrompt = false;
    }

    signOut() {
        this.store.dispatch(AuthActions.signOut({ redirectTo: '/' }));
    }

    toggleMobile() {
        this.openMobile = !this.openMobile;
        if (this.openMobile) {
            this.openCurrency = false;
            this.openProfile = false;
            this.openSignup = false;
        }
    }

    closeMobile() {
        this.openMobile = false;
        this.openCurrency = false;
        this.openProfile = false;
        this.openSignup = false;
    }

    @HostListener('document:click', ['$event'])
    closeOnOutside(event: Event): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.openCurrency = false;
            this.openProfile = false;
            this.openSignup = false;
        }
    }

    handleAvatarError(): void {
        this.avatarLoadFailed = true;
        this.clearAvatarUrl();
    }

    goToDashboard() {
        forkJoin({
            authed: this.isAuthenticated$.pipe(take(1)),
            actor: this.store.select(selectActor).pipe(take(1))
        }).subscribe(({ authed, actor }) => {
            if (authed) {
                const roleCode = String(actor?.role_code || '').trim().toLowerCase();
                const target =
                    roleCode === 'app_admin'
                        ? '/admin/dashboard'
                        : actor?.actor_type === 'investor'
                            ? '/customer-dashboard/reservations'
                            : '/developer/dashboard';
                this.router.navigate([target]).catch(() => { });
            } else {
                this.router.navigate(['/login'], { queryParams: { returnUrl: '/developer/dashboard' } });
            }
            this.openProfile = false;
        });
    }

    goToSignup(type: 'customer' | 'developer') {
        this.router.navigate(['/signup', type]);
        this.openSignup = false;
    }

    goToSignupMain() {
        this.router.navigate(['/signup']);
    }

  ngOnInit(): void {
    this.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
            const userId = user?.user_id || user?.id || null;
            if (this.lastAvatarUserId !== (userId || null)) {
                this.lastAvatarUserId = userId || null;
                this.avatarLoadFailed = false;
            }

            const directUrl =
                user?.profile?.PROFILE_PICTURE_URL ||
                user?.profile?.profile_picture_url ||
                user?.profile_picture_url ||
                null;

            const fileId = this.normalizeId(
                user?.profile_picture_file_id ||
                user?.profile?.profile_picture_file_id ||
                user?.profile?.PROFILE_PICTURE_FILE_ID
            );

            if (directUrl && directUrl.trim() !== '') {
                this.setAvatarUrl(directUrl);
                return;
            }

            if (fileId && fileId !== this.lastAvatarFileId && !this.avatarLoadFailed) {
                this.lastAvatarFileId = fileId;
                this.fetchAvatar(fileId);
                return;
            }

            if (!fileId && !directUrl) {
                this.clearAvatarUrl();
            }
        });
    }

  ngAfterViewInit(): void {
    const el = this.elementRef.nativeElement as HTMLElement;
    const headerEl = el.querySelector?.('header') as HTMLElement | null;
    if (!headerEl || typeof ResizeObserver === 'undefined') return;
    const update = () => this.syncHeaderHeightOffset(headerEl);
    this.headerResizeObserver = new ResizeObserver(update);
    this.headerResizeObserver.observe(headerEl);
    update();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const el = this.elementRef.nativeElement as HTMLElement;
    const headerEl = el.querySelector?.('header') as HTMLElement | null;
    if (!headerEl) return;
    this.syncHeaderHeightOffset(headerEl);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearAvatarUrl();
    this.headerResizeObserver?.disconnect();
    this.headerResizeObserver = null;
    document.body.style.removeProperty('--mobile-header-h');
  }

  private syncHeaderHeightOffset(headerEl: HTMLElement): void {
    const h = headerEl.offsetHeight;
    if (h > 0 && typeof window !== 'undefined' && window.innerWidth < 1280) {
      document.body.style.setProperty('--mobile-header-h', h + 'px');
    } else {
      document.body.style.removeProperty('--mobile-header-h');
    }
  }

    private fetchAvatar(fileId: string): void {
        this.filesService.downloadPrivateFile(fileId).subscribe({
            next: (blob) => {
                const url = URL.createObjectURL(blob);
                this.setAvatarUrl(url);
            },
            error: () => {
                this.avatarLoadFailed = true;
                this.clearAvatarUrl();
            }
        });
    }

    private setAvatarUrl(url: string): void {
        if (this.avatarUrl && this.avatarUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.avatarUrl);
        }
        this.avatarUrl = url;
        this.avatarLoadFailed = false;
    }

    private clearAvatarUrl(): void {
        if (this.avatarUrl && this.avatarUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.avatarUrl);
        }
        this.avatarUrl = null;
    }

    private normalizeId(value: unknown): string | null {
        const raw = String(value || '').trim();
        return raw ? raw : null;
    }
}
